import React, { ReactNode } from "react";
import LinkedText from "../../../shared/components/ui/LinkedText";
import { Box } from "../../../shared/components/ui/chakra";
import FacebookIcon from "../../../shared/components/icons/FacebookIcon";
import InstagramIcon from "../../../shared/components/icons/InstagramIcon";
import NoteIcon from "../../../shared/components/icons/NoteIcon";
import PinterestIcon from "../../../shared/components/icons/PinterestIcon";
import ThreadsIcon from "../../../shared/components/icons/ThreadsIcon";
import TikTokIcon from "../../../shared/components/icons/TikTokIcon";
import XIcon from "../../../shared/components/icons/XIcon";
import YoutubeIcon from "../../../shared/components/icons/YoutubeIcon";
import { BusinessEntity } from "../../business_entities/lib/types";
import Accordion from "../../business_entities/components/Accordion";
import { BusinessEntityChip } from "../../business_entities/components/BusinessEntityChip";
import BusinessEntityContentsCard from "../../business_entities/components/BusinessEntityContentsCard";
import BusinessEntityDesignWorkCard from "../../business_entities/components/BusinessEntityDesignWorkCard";
import BusinessEntityDottedLine from "../../business_entities/components/BusinessEntityDottedLine";
import BusinessEntitySectionTitle from "../../business_entities/components/BusinessEntitySectionTitle";
import BusinessEntitySideMenu from "../../business_entities/components/BusinessEntitySideMenu";
import BusinessEntityUseCaseTags from "../../business_entities/components/BusinessEntityUseCaseTags";
import BusinessEntitySummary from "../../business_entities/components/BusinessEntitySummary";
import { SharedApprovedCurrentUser } from "../../../shared/lib/types";
import GoBackLink from "../../../shared/components/app/GoBackLink";
import { businessMatchingRootPath } from "../../../../../routes";

import { Container, Flex, Grid, GridItem, HStack, Show, Stack } from "@chakra-ui/react";

const BusinessEntityShowContent = ({
  businessEntity,
  currentUser,
  preview = false,
  disableBackLink = false,
}: {
  businessEntity: BusinessEntity;
  currentUser?: SharedApprovedCurrentUser;
  preview?: boolean;
  disableBackLink?: boolean,
}) => {
  const backUrl = () =>
    sessionStorage.getItem("previousBusinessEntitiesIndexUrl") ??
    businessMatchingRootPath();
  return (
    <Box position="relative">
      <Box position="relative" zIndex={1}>
        <Container maxW="container.lg" mt={{ base: 6, md: 20 }} px={5}>
          {!preview && !disableBackLink && (
            <GoBackLink href={backUrl()}>事業者一覧に戻る</GoBackLink>
          )}
          <Grid
            mt={4}
            templateColumns={{
              base: "100%",
              lg: "minmax(0, 768px) 232px",
            }}
            rowGap={5}
            columnGap={6}
            maxW="100vw"
          >
            <GridItem colSpan={{ base: 1, lg: 2 }}>
              <HStack flexWrap="wrap" rowGap={1} w="full">
                <Box as="h5" fontSize={{ base: "xs", md: "sm" }}>
                  利用用途：
                </Box>
                <BusinessEntityUseCaseTags
                  useCase={
                    businessEntity.use_case as "contractor" | "client" | "both"
                  }
                />
              </HStack>
            </GridItem>
            <GridItem>
              <Stack gap={{ base: 10, md: 16 }} id="summary">
                <BusinessEntitySummary {...businessEntity} />
                <Box as="section" id="conditions">
                  <BusinessEntitySectionTitle as="h2">
                    業務の希望条件
                  </BusinessEntitySectionTitle>
                  <Stack gap={{ base: 3, md: 4 }}>
                    {(businessEntity.use_case === "contractor" ||
                      businessEntity.use_case === "both") &&
                      businessEntity.contractor_info && (
                        <Accordion title="受注したい業務" initialOpen>
                          <CardRows
                            rows={[
                              <Box as="section" key="受注したい業種">
                                <CardRowTitle>受注したい業種</CardRowTitle>
                                <HStack
                                  rowGap={2}
                                  columnGap={1}
                                  flexWrap="wrap"
                                >
                                  {businessEntity.contractor_info.desired_industries.map(
                                    ({ name, id }) => (
                                      <BusinessEntityChip key={id}>
                                        {name}
                                      </BusinessEntityChip>
                                    ),
                                  )}
                                </HStack>
                              </Box>,
                              <Box as="section" key="対応できる分野">
                                <CardRowTitle>対応できる分野</CardRowTitle>
                                <HStack
                                  rowGap={2}
                                  columnGap={1}
                                  flexWrap="wrap"
                                >
                                  {businessEntity.contractor_info.supported_business_fields.map(
                                    ({ name, id }) => (
                                      <BusinessEntityChip key={id}>
                                        {name}
                                      </BusinessEntityChip>
                                    ),
                                  )}
                                </HStack>
                              </Box>,
                              <Box as="section" key="対応できる業務内容">
                                <CardRowTitle>対応できる業務内容</CardRowTitle>
                                <Box
                                  whiteSpace="pre-wrap"
                                  fontSize={{ base: "xs", md: "sm" }}
                                >
                                  <LinkedText>
                                    {
                                      businessEntity.contractor_info
                                        .supported_business_service_note
                                    }
                                  </LinkedText>
                                </Box>
                              </Box>,
                              <Box
                                as="section"
                                key="対応できる建物種別・構造種別"
                              >
                                <CardRowTitle>
                                  対応できる建物種別・構造種別
                                </CardRowTitle>
                                <CardRowGroups
                                  groups={businessEntity.contractor_info.supported_building_types.map(
                                    ({ id, name, structure_types }) => (
                                      <CardRowGroup key={id} name={name}>
                                        {structure_types.map(({ id, name }) => (
                                          <BusinessEntityChip key={id}>
                                            {name}
                                          </BusinessEntityChip>
                                        ))}
                                      </CardRowGroup>
                                    ),
                                  )}
                                />
                              </Box>,
                              <Box as="section" key="対応可能エリア">
                                <CardRowTitle>対応可能エリア</CardRowTitle>
                                <Stack gap={4}>
                                  {businessEntity.contractor_info.supported_prefectures.map(
                                    ({ region, prefectures }) => (
                                      <Box key={region}>
                                        <Box
                                          as="h5"
                                          color="brand.500"
                                          fontSize="xs"
                                          borderBottom="1px solid"
                                          borderColor="brand.200"
                                          pb={1}
                                        >
                                          {region}
                                        </Box>
                                        <Box fontSize="sm" mt={2}>
                                          {prefectures
                                            .map(({ name }) => name)
                                            .join(" / ")}
                                        </Box>
                                      </Box>
                                    ),
                                  )}
                                </Stack>
                              </Box>,
                            ]}
                          />
                        </Accordion>
                      )}
                    {(businessEntity.use_case === "client" ||
                      businessEntity.use_case === "both") &&
                      businessEntity.client_info && (
                        <Accordion title="発注したい業務" initialOpen>
                          <CardRows
                            rows={[
                              <Box as="section" key="発注したい業種">
                                <CardRowTitle>発注したい業種</CardRowTitle>
                                <HStack
                                  rowGap={2}
                                  columnGap={1}
                                  flexWrap="wrap"
                                >
                                  {businessEntity.client_info.desired_industries.map(
                                    ({ name, id }) => (
                                      <BusinessEntityChip key={id}>
                                        {name}
                                      </BusinessEntityChip>
                                    ),
                                  )}
                                </HStack>
                              </Box>,
                              <Box as="section" key="発注したい分野">
                                <CardRowTitle>発注したい分野</CardRowTitle>
                                <HStack
                                  rowGap={2}
                                  columnGap={1}
                                  flexWrap="wrap"
                                >
                                  {businessEntity.client_info.desired_business_fields.map(
                                    ({ name, id }) => (
                                      <BusinessEntityChip key={id}>
                                        {name}
                                      </BusinessEntityChip>
                                    ),
                                  )}
                                </HStack>
                              </Box>,
                              <Box as="section" key="発注したい業務内容">
                                <CardRowTitle>発注したい業務内容</CardRowTitle>
                                <Box
                                  whiteSpace="pre-wrap"
                                  fontSize={{ base: "xs", md: "sm" }}
                                >
                                  <LinkedText>
                                    {
                                      businessEntity.client_info
                                        .desired_business_service_note
                                    }
                                  </LinkedText>
                                </Box>
                              </Box>,
                            ]}
                          />
                        </Accordion>
                      )}
                  </Stack>
                </Box>
                <Stack>
                  {(businessEntity.use_case === "contractor" ||
                    businessEntity.use_case === "both") &&
                    businessEntity.contractor_info &&
                    businessEntity.contractor_info.architectural_design_works
                      .length > 0 && (
                      <Box as="section" id="works">
                        <BusinessEntitySectionTitle as="h2">
                          設計事例・実績
                        </BusinessEntitySectionTitle>
                        <Stack gap={{ base: 5, md: 10 }}>
                          {businessEntity.contractor_info.architectural_design_works.map(
                            (designWork, index) => (
                              <BusinessEntityDesignWorkCard
                                designWork={designWork}
                                key={index}
                              />
                            ),
                          )}
                        </Stack>
                      </Box>
                    )}
                  <BusinessEntityDottedLine mt={{ base: 6, md: 5 }} />
                  {(businessEntity.main_client !== "" ||
                    businessEntity.used_architectural_software_categories
                      .length > 0) && (
                    <Stack gap={3} mt={{ base: 6, md: 5 }}>
                      {businessEntity.used_architectural_software_categories
                        .length > 0 && (
                        <BusinessEntityContentsCard title="利用している建築系ソフトウェア">
                          <CardRows
                            rows={businessEntity.used_architectural_software_categories.map(
                              ({ category, softwares }) => (
                                <CardRowGroup key={category} name={category}>
                                  {softwares.map(({ name, id }) => (
                                    <BusinessEntityChip key={id}>
                                      {name}
                                    </BusinessEntityChip>
                                  ))}
                                </CardRowGroup>
                              ),
                            )}
                          />
                        </BusinessEntityContentsCard>
                      )}
                      {businessEntity.main_client !== "" && (
                        <BusinessEntityContentsCard title="主な取引先">
                          <Box whiteSpace="pre-wrap">
                            {businessEntity.main_client}
                          </Box>
                        </BusinessEntityContentsCard>
                      )}
                    </Stack>
                  )}
                </Stack>
                <Box as="section" id="business-entity-info">
                  <BusinessEntitySectionTitle as="h2">
                    事業者情報
                  </BusinessEntitySectionTitle>
                  <BusinessEntityContentsCard>
                    <CardRows
                      rows={[
                        <CardRowGroup key="事業者名" name="事業者名">
                          <Box>
                            {businessEntity.name}
                            {businessEntity.branch_name && (
                              <>（{businessEntity.branch_name}）</>
                            )}
                          </Box>
                        </CardRowGroup>,
                        <CardRowGroup key="事業者区分" name="事業者区分">
                          <Box>{businessEntity.business_type}</Box>
                        </CardRowGroup>,
                        <CardRowGroup key="所在地" name="所在地">
                          <Box>
                            {businessEntity.postal_code && (
                              <>
                                〒{businessEntity.postal_code}
                                <br />
                              </>
                            )}
                            {businessEntity.prefecture.name}
                            {businessEntity.address1}
                            {businessEntity.address2}
                            {businessEntity.address3}
                          </Box>
                        </CardRowGroup>,
                        <CardRowGroup key="主業種" name="主業種">
                          <Box>{businessEntity.industry.name}</Box>
                        </CardRowGroup>,
                        <CardRowGroup key="従業員数" name="従業員数">
                          <Box>{businessEntity.number_of_employees}名</Box>
                        </CardRowGroup>,
                        <CardRowGroup key="設立" name="設立">
                          <Box>
                            {
                              businessEntity.establishment_year_month.split(
                                "/",
                              )[0]
                            }
                            年
                            {
                              businessEntity.establishment_year_month.split(
                                "/",
                              )[1]
                            }
                            月
                          </Box>
                        </CardRowGroup>,
                        <CardRowGroup key="資本金" name="資本金">
                          <Box>
                            {businessEntity.capital_ten_thousand_yen === 0
                              ? "-"
                              : `${businessEntity.capital_ten_thousand_yen}万円`}
                          </Box>
                        </CardRowGroup>,
                        <CardRowGroup key="代表者氏名" name="代表者氏名">
                          <Box>{businessEntity.president_name}</Box>
                        </CardRowGroup>,
                        businessEntity.website_url !== "" ? (
                          <CardRowGroup key="Webサイト" name="Webサイト">
                            <Box>
                              <LinkedText>
                                {businessEntity.website_url}
                              </LinkedText>
                            </Box>
                          </CardRowGroup>
                        ) : null,
                        (
                          [
                            "facebook_url",
                            "instagram_url",
                            "note_url",
                            "pinterest_url",
                            "threads_url",
                            "tiktok_url",
                            "x_url",
                            "youtube_url",
                          ] as const
                        ).some((key) => businessEntity[key] !== "") ? (
                          <CardRowGroup key="SNS" name="SNS">
                            {businessEntity.facebook_url !== "" && (
                              <Box
                                as="a"
                                href={businessEntity.facebook_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FacebookIcon />
                              </Box>
                            )}
                            {businessEntity.instagram_url !== "" && (
                              <Box
                                as="a"
                                href={businessEntity.instagram_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <InstagramIcon />
                              </Box>
                            )}
                            {businessEntity.note_url !== "" && (
                              <Box
                                as="a"
                                href={businessEntity.note_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <NoteIcon />
                              </Box>
                            )}
                            {businessEntity.pinterest_url !== "" && (
                              <Box
                                as="a"
                                href={businessEntity.pinterest_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <PinterestIcon />
                              </Box>
                            )}
                            {businessEntity.threads_url !== "" && (
                              <Box
                                as="a"
                                href={businessEntity.threads_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <ThreadsIcon />
                              </Box>
                            )}
                            {businessEntity.tiktok_url !== "" && (
                              <Box
                                as="a"
                                href={businessEntity.tiktok_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <TikTokIcon />
                              </Box>
                            )}
                            {businessEntity.x_url !== "" && (
                              <Box
                                as="a"
                                href={businessEntity.x_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <XIcon />
                              </Box>
                            )}
                            {businessEntity.youtube_url !== "" && (
                              <Box
                                as="a"
                                href={businessEntity.youtube_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <YoutubeIcon />
                              </Box>
                            )}
                          </CardRowGroup>
                        ) : null,
                      ].filter(Boolean)}
                    />
                  </BusinessEntityContentsCard>
                </Box>
              </Stack>
            </GridItem>
            <Show above="lg">
              <GridItem>
                <Box
                  as="nav"
                  minW="232px"
                  position="sticky"
                  top="144px"
                  h="fit-content"
                >
                  <BusinessEntitySideMenu
                    preview={preview}
                    businessEntity={businessEntity}
                    currentUser={currentUser}
                  />
                </Box>
              </GridItem>
            </Show>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

const CardRows = ({ rows }: { rows: ReactNode[] }) => (
  <Stack gap={6}>
    {rows.map((row, index) => (
      <React.Fragment key={index}>
        <Box>{row}</Box>
        {index < rows.length - 1 && <BusinessEntityDottedLine />}
      </React.Fragment>
    ))}
  </Stack>
);

const CardRowTitle = ({ children }: { children: ReactNode }) => (
  <Box
    as="h4"
    fontSize={{ base: "md", md: "lg" }}
    fontWeight="bold"
    mb={{ base: 3, md: 4 }}
  >
    {children}
  </Box>
);

const CardRowGroup = ({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) => (
  <Flex direction={{ base: "column", md: "row" }} rowGap={2}>
    <Box as="h5" fontWeight="bold" minW={{ base: "initial", md: "200px" }}>
      {name}
    </Box>
    <HStack
      flex={1}
      gap={2}
      flexWrap="wrap"
      fontSize={{ base: "sm", md: "md" }}
    >
      {children}
    </HStack>
  </Flex>
);

const CardRowGroups = ({ groups }: { groups: ReactNode[] }) => (
  <Stack gap={6}>
    {groups.map((group, index) => (
      <Box key={index}>{group}</Box>
    ))}
  </Stack>
);

export default BusinessEntityShowContent;
